import { decryptString } from '@47ng/cloak'
import type { Project } from '@prisma/client'
import { api } from 'client/services/api'
import { useKeystore } from 'client/state/stores/keystore'
import { omit } from 'modules/std/objects'
import type { IntervalQuery } from 'modules/time-navigation/defs'
import { shiftTime } from 'modules/time-navigation/navigation'
import React from 'react'
import {
  QueryFunctionContext,
  useQuery,
  useQueryClient,
  UseQueryOptions
} from 'react-query'
import type { ListProjectsReply } from 'server/routes/projects'
import { clientQueryKeys, serverQueryKeys } from './queryKeys'

export interface VaultCacheEntry {
  vaultID: string
  vaultKey: string
}

// Server state queries --

export function useProjectsQuery(options: UseQueryOptions<Project[]> = {}) {
  const queryClient = useQueryClient()
  const keystore = useKeystore()
  const hasKeychainKey = Boolean(keystore.get('keychainKey'))

  return useQuery({
    queryKey: serverQueryKeys.projects,
    queryFn: async function getProjectsList(): Promise<Project[]> {
      const keychainKey = keystore.get('keychainKey')
      if (!keychainKey) {
        throw new Error('Failed to read keychain key')
      }
      const res = await api.get<{ Reply: ListProjectsReply }>('/projects')
      // Start prefetching local usages while we decrypt the keys
      res.data.forEach(project => {
        queryClient.prefetchQuery(
          clientQueryKeys.projectUsage(project.id),
          fetchProjectUsage
        )
      })

      // Then unlock projects
      return Promise.all<Project>(
        res.data.map(async ({ vaultKey, ...project }) => {
          const vaultQueryKey = serverQueryKeys.vault(project.vaultID)
          // First, see if the unlocked vault is already in the cache
          let vault = queryClient.getQueryData<VaultCacheEntry>(vaultQueryKey)
          if (!vault) {
            // If it isn't, decrypt its key
            vault = {
              vaultID: project.vaultID,
              vaultKey: await decryptString(vaultKey, keychainKey)
            }
            // And update the vault cache
            queryClient.setQueryData<VaultCacheEntry>(vaultQueryKey, vault)
            queryClient.setQueryData<VaultCacheEntry[]>(
              serverQueryKeys.vaults,
              vaults => Array.from(new Set([...(vaults ?? []), vault!]))
            )
          }
          // Now we can unlock the project
          return {
            ...project,
            secretKey: await decryptString(project.secretKey, vault.vaultKey)
          }
        })
      )
    },
    enabled: hasKeychainKey,
    ...options
  })
}

export function useProjectQuery(projectID: Project['id']) {
  const queryClient = useQueryClient()
  return useQuery({
    queryKey: serverQueryKeys.project(projectID),
    queryFn: fetchProject,
    enabled: Boolean(projectID),
    initialData: () => {
      const allProjects = queryClient.getQueryData<Project[]>(
        serverQueryKeys.projects,
        {
          exact: true
        }
      )
      const project = allProjects?.find(project => project.id === projectID)
      return project ? omit(project, ['secretKey']) : undefined
    }
  })
}

// Client state queries --

export function useProjectUsageQuery(projectID: Project['id']) {
  return useQuery({
    queryKey: clientQueryKeys.projectUsage(projectID),
    queryFn: fetchProjectUsage,
    refetchInterval: 5_000,
    enabled: Boolean(projectID)
    // todo: Upgrade to React Query v4 and add networkMode: 'always'
  })
}

export function useProjectAnalyticsQuery(
  projectID: Project['id'],
  interval: IntervalQuery
) {
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: clientQueryKeys.projectAnalyticsInterval(projectID, interval),
    queryFn: fetchProjectAnalytics,
    enabled: Boolean(projectID)
    // todo: Upgrade to React Query v4 and add networkMode: 'always'
  })

  // Prefetch the previous interval to speed up navigation,
  // but only once the main query has resolved.
  React.useEffect(() => {
    if (!projectID || query.status !== 'success') {
      return
    }
    const previousInterval: IntervalQuery = {
      from: shiftTime(interval.from, interval.duration, 'past'),
      duration: interval.duration
    }
    queryClient.prefetchQuery({
      queryKey: clientQueryKeys.projectAnalyticsInterval(
        projectID,
        previousInterval
      ),
      queryFn: fetchProjectAnalytics
    })
  }, [queryClient, interval, projectID, query.status])

  return query
}

// Server fetching methods --

async function fetchProject({
  queryKey
}: QueryFunctionContext<ReturnType<typeof serverQueryKeys.project>>) {
  const projectID = queryKey[2]
  const res = await api.get<{ Reply: Project }>(`/projects/${projectID}`)
  return res.data
}

// Client fetching methods (calling into the analysis worker) --

async function fetchProjectUsage({
  queryKey
}: QueryFunctionContext<ReturnType<typeof clientQueryKeys.projectUsage>>) {
  const projectID = queryKey[2]
  const { analysisWorker } = await import('client/workers/analysis.controller')
  const usage = await analysisWorker.getProjectUsage(projectID, 7)
  return usage
}

async function fetchProjectAnalytics({
  queryKey
}: QueryFunctionContext<
  ReturnType<typeof clientQueryKeys.projectAnalyticsInterval>
>) {
  const projectID = queryKey[2]
  const intervalQuery = queryKey[4]
  const { analysisWorker } = await import('client/workers/analysis.controller')
  return analysisWorker.getProjectAnalytics(projectID, intervalQuery)
}
